import { logger } from '@heidi/runtime/logger';
import { TrackingKey } from '@heidi/runtime/performance';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from 'jotai';
import type { UpdateStructureFromExampleRequest } from '../../contracts';
import {
  GENERATE_STRUCTURE_FROM_EXAMPLE_NOTE_STREAMING_KEY,
  GET_TEMPLATE_CREATION_RECORD_BY_ID,
} from '../../contracts';
import { structureGenerationStreamStateAtomFamily } from './atoms';
import { trackStreamRun } from './streamPerfTracking';
import {
  appendToVersionControlField,
  createStreamStateHandlers,
  updateStreamStates,
  updateVersionControlInQueryData,
} from './streamStateHelpers';
import { streamUpdateStructureFromNote } from './streaming';
import { type AnimationType, StreamStatus } from './types';

export { GENERATE_STRUCTURE_FROM_EXAMPLE_NOTE_STREAMING_KEY };

export interface GenerateStructureFromExampleNoteMutationRequest extends UpdateStructureFromExampleRequest {
  template_creation_record_id: string;
  target_version_control_id: string;
  animationType: AnimationType;
}

export const useMutateGenerateStructureFromExampleNote = () => {
  const queryClient = useQueryClient();
  const store = useStore();

  return useMutation({
    mutationKey: [GENERATE_STRUCTURE_FROM_EXAMPLE_NOTE_STREAMING_KEY],
    onMutate: ({
      template_creation_record_id: templateCreationRecordId,
      target_version_control_id: versionControlId,
      animationType,
    }) => {
      const streamStateAtom = structureGenerationStreamStateAtomFamily({
        templateCreationRecordId,
        versionControlId,
      });
      updateStreamStates({
        store,
        atoms: [streamStateAtom],
        status: StreamStatus.WAITING_START,
        animationType,
      });
      return { streamStateAtom };
    },
    mutationFn: async ({
      template_creation_record_id: templateCreationRecordId,
      target_version_control_id: versionControlId,
      base_template_version_control_id,
      edited_example_note,
      current_template_structure,
      user_request,
      animationType,
    }: GenerateStructureFromExampleNoteMutationRequest) => {
      const streamStateAtom = structureGenerationStreamStateAtomFamily({
        templateCreationRecordId,
        versionControlId,
      });

      const streamState = store.get(streamStateAtom);
      const queryKey = [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }];

      return trackStreamRun(
        TrackingKey.TEMPLATE_STRUCTURE_FROM_NOTE,
        { animationType },
        async (markFirstToken) => {
          let accumulatedStructure = '';

          let isFirstChunk = true;

          await streamUpdateStructureFromNote(
            {
              data: {
                base_template_version_control_id,
                edited_example_note,
                current_template_structure,
                user_request,
              },
              templateCreationRecordId,
              targetVersionControlId: versionControlId,
              signal: streamState.abortController.signal,
            },
            createStreamStateHandlers({
              store,
              streamStateAtom,
              onMessageCallback: (chunk) => {
                if (isFirstChunk) {
                  markFirstToken();
                  updateStreamStates({
                    store,
                    atoms: [streamStateAtom],
                    status: StreamStatus.STREAMING,
                  });
                  updateVersionControlInQueryData({
                    queryClient,
                    queryKey,
                    versionControlId,
                    field: 'template_structure',
                    updater: () => '',
                  });
                  isFirstChunk = false;
                }
                accumulatedStructure += chunk.data;
                appendToVersionControlField({
                  queryClient,
                  queryKey,
                  versionControlId,
                  field: 'template_structure',
                  chunk,
                });
              },
            })
          );

          return accumulatedStructure;
        }
      );
    },
    onSuccess: (_, __, context) => {
      const { streamStateAtom } = context ?? {};
      if (streamStateAtom) {
        updateStreamStates({
          store,
          atoms: [streamStateAtom],
          status: StreamStatus.IDLE,
        });
      }
    },
    onError: (error: Error, _, context) => {
      const { streamStateAtom } = context ?? {};
      if (streamStateAtom) {
        updateStreamStates({
          store,
          atoms: [streamStateAtom],
          status: StreamStatus.ERROR,
        });
      }
      logger.error(error);
    },
  });
};
